import { useEffect, useRef, useState } from 'react';
import {
  evalEntrance,
  type AnimationSpec,
  idleMotionAt,
  unitMotionAt,
  MOTION_KIND_DEFAULTS,
  type HookMotion,
  type HookMotionKind,
  type HookIdle,
  type WordMotionSpec,
} from '@captionflow/caption-engine';
import { IN_KINDS, IDLES, PhaseControls, Slider, pct } from './controls';

const still: WordMotionSpec = {
  in: { kind: 'none', durationMs: 0, distance: 0, blur: 0, easing: 'linear' },
  hold: 'none',
  holdAmount: 0,
};
const common = new Set(['fade', 'slideUp', 'zoomIn', 'punch']);

/** Small demonstrations use the same motion evaluator as the video renderer. */
function MotionSample({
  text,
  spec,
  captionEntrance,
  fontFamily,
  fontWeight,
}: {
  text: string;
  captionEntrance?: AnimationSpec;
  spec?: WordMotionSpec | null;
  fontFamily: string;
  fontWeight: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || (!spec && !captionEntrance)) return;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const motion: HookMotion = {
      in: spec?.in ?? still.in,
      out: { ...still.in },
      split: 'block',
      staggerMs: 0,
      idle: spec?.hold ?? 'none',
      idleAmount: spec?.holdAmount ?? 0,
    };
    let raf = 0;
    let visible = false;
    let start = performance.now();
    const draw = (now: number) => {
      const time = reduced.matches
        ? 1800
        : (now - start) %
          Math.max(2200, (spec?.in.durationMs ?? captionEntrance?.durationMs ?? 450) + 1000);
      const state = unitMotionAt(motion, { startMs: 0, endMs: 10000 }, 0, time, {
        width: 300,
        height: 480,
        fontPx: 22,
      });
      const hold = idleMotionAt(motion, { startMs: 0, endMs: 2200 }, time, {
        width: 300,
        height: 480,
      });
      if (!spec && captionEntrance) {
        const caption = evalEntrance(captionEntrance, time);
        state.opacity = caption.opacity;
        state.scale = caption.scale;
        state.tx = caption.translateXPx * 0.25;
        state.ty = caption.translateYPx * 0.25;
        state.rotateDeg = caption.rotateDeg;
        state.blurPx = caption.blurPx * 0.25;
      }
      el.style.opacity = String(state.opacity);
      el.style.transform = `translate(${state.tx + hold.tx}px, ${state.ty + hold.ty + state.maskOffset * 28}px) rotate(${state.rotateDeg + hold.rotateDeg}deg) scale(${state.scale * hold.scale})`;
      el.style.filter = `blur(${state.blurPx}px)`;
      el.style.letterSpacing = `${state.trackEm}em`;
      if (visible && !reduced.matches && !document.hidden) raf = requestAnimationFrame(draw);
    };
    const update = () => {
      cancelAnimationFrame(raf);
      if (visible && !document.hidden) {
        start = performance.now();
        draw(start);
      }
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry?.isIntersecting ?? false;
      update();
    });
    observer.observe(el.parentElement ?? el);
    reduced.addEventListener('change', update);
    document.addEventListener('visibilitychange', update);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
      reduced.removeEventListener('change', update);
      document.removeEventListener('visibilitychange', update);
    };
  }, [spec, captionEntrance]);
  return (
    <span className="si-motion-sample" aria-hidden="true">
      <span ref={ref} style={{ fontFamily, fontWeight }}>
        {text.slice(0, 18)}
      </span>
    </span>
  );
}

export function WordAnimationEditor({
  value,
  inherited,
  captionEntrance,
  text,
  fontFamily,
  fontWeight,
  onChange,
  onReplay,
}: {
  value?: WordMotionSpec | null;
  inherited?: WordMotionSpec;
  captionEntrance?: AnimationSpec;
  text: string;
  fontFamily: string;
  fontWeight: number;
  onChange: (motion: WordMotionSpec | null) => void;
  onReplay: () => void;
}) {
  const [all, setAll] = useState(false);
  const current = value?.in.kind;
  const isStill = value?.in.kind === 'none' && value.hold === 'none';
  const apply = (motion: WordMotionSpec | null) => {
    onChange(motion);
    onReplay();
  };
  const entrance = (kind: HookMotionKind): WordMotionSpec => ({
    in: {
      kind,
      ...MOTION_KIND_DEFAULTS[kind],
      durationMs: 450,
      ...(kind.startsWith('slide') ? { distance: 0.05 } : {}),
    },
    hold: value?.hold ?? 'none',
    holdAmount: value?.holdAmount ?? 0.5,
  });
  return (
    <div className="si-animation">
      <div className="si-animation-heading">
        <span>Entrance</span>
        <button className="si-linkbtn" onClick={onReplay}>
          Replay in video ↻
        </button>
      </div>
      <div className="si-motion-grid">
        <button
          className={`si-motion-tile ${!value ? 'on' : ''}`}
          aria-pressed={!value}
          onClick={() => apply(null)}
        >
          <MotionSample
            text={text}
            spec={inherited}
            captionEntrance={captionEntrance}
            fontFamily={fontFamily}
            fontWeight={fontWeight}
          />
          <span>Use caption animation</span>
        </button>
        <button
          className={`si-motion-tile ${isStill ? 'on' : ''}`}
          aria-pressed={isStill}
          onClick={() => apply(still)}
        >
          <MotionSample text={text} fontFamily={fontFamily} fontWeight={fontWeight} />
          <span>No animation</span>
        </button>
        {IN_KINDS.filter(
          (option) => option.v !== 'none' && (all || common.has(option.v) || current === option.v),
        ).map((option) => {
          const spec = current === option.v && value ? value : entrance(option.v);
          return (
            <button
              key={option.v}
              className={`si-motion-tile ${current === option.v ? 'on' : ''}`}
              aria-pressed={current === option.v}
              onClick={() => apply(spec)}
            >
              <MotionSample
                text={text}
                spec={spec}
                fontFamily={fontFamily}
                fontWeight={fontWeight}
              />
              <span>{option.label}</span>
            </button>
          );
        })}
      </div>
      <button className="si-linkbtn" aria-expanded={all} onClick={() => setAll(!all)}>
        {all ? 'Show fewer effects' : 'View all effects'}
      </button>
      {value && current !== 'none' && (
        <details className="si-group si-motion-settings">
          <summary>Adjust entrance</summary>
          <div className="si-group-body">
            <PhaseControls
              phase={value.in}
              kind={value.in.kind}
              onChange={(patch) => onChange({ ...value, in: { ...value.in, ...patch } })}
            />
          </div>
        </details>
      )}
      <details className="si-group">
        <summary>
          While on screen{' '}
          {value && value.hold !== 'none' && <span className="si-custom">Custom</span>}
        </summary>
        <div className="si-group-body">
          <div className="si-motion-grid">
            {IDLES.map((option) => {
              const spec: WordMotionSpec = {
                in: still.in,
                hold: option.v as HookIdle,
                holdAmount: value?.holdAmount ?? 0.5,
              };
              const active = (value?.hold ?? 'none') === option.v;
              return (
                <button
                  key={option.v}
                  className={`si-motion-tile ${active ? 'on' : ''}`}
                  aria-pressed={active}
                  onClick={() => apply({ ...spec, in: value?.in ?? still.in })}
                >
                  <MotionSample
                    text={text}
                    spec={spec}
                    fontFamily={fontFamily}
                    fontWeight={fontWeight}
                  />
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
          {value && value.hold !== 'none' && (
            <Slider
              label="Intensity"
              min={0}
              max={1}
              step={0.02}
              value={value.holdAmount}
              display={pct(value.holdAmount)}
              onChange={(holdAmount) => onChange({ ...value, holdAmount })}
            />
          )}
        </div>
      </details>
    </div>
  );
}
